import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { TextInput, SelectInput, CountrySelectDropdown, Alert } from "../../../packages/shared/";
import { Pattern, AxiosGraphQlAction, GENDERS, Helper } from "../../services";
import { connect } from "react-redux";
import { getLmbcList, setLmbc } from "../reducers";
import { type AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { FormContainer } from "../../../components/shared";
import { ERROR_CODE_LIST as codeList } from "../../../utils";

interface Props {
    formType?: string;
    query: string;
    formData?: FormData;
    lmbc: any;
    setLmbc: (list: any) => void;
}

type FormData = Record<string, string>;

function Form(props: Props) {
    const { formType, query, formData } = props;
    const defaultValues = {
        ...formData,
    };

    const defaultAlert = {
        message: "",
        type: "",
    };
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(defaultAlert);
    const setAlertToDefault = () =>
        setTimeout(() => {
            setAlert(defaultAlert);
        }, 3000);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues,
    });

    const onSubmit = handleSubmit((data) => {
        setLoading(true);

        const variables = {
            lmbcHcp: {
                ...data,
                monday: Helper.strToArray(data.monday),
                tuesday: Helper.strToArray(data.tuesday),
                wednesday: Helper.strToArray(data.wednesday),
                thursday: Helper.strToArray(data.thursday),
                friday: Helper.strToArray(data.friday),
                saturday: Helper.strToArray(data.saturday),
                sunday: Helper.strToArray(data.sunday),
            },
        };

        AxiosGraphQlAction(variables, query)
            .then((response: AxiosResponse) => {
                if (response.data.errors) {
                    const errorMessage =
                        // @ts-expect-error: Unreachable code error
                        codeList[response?.data?.errors[0]?.errcode] || "Something went wrong";
                    setAlert({
                        message: errorMessage,
                        type: "danger",
                    });
                } else {
                    formType !== "update" && reset();
                    setAlert({
                        message: `Successfully ${formType === "update" ? "Updated" : "Created"}`,
                        type: "success",
                    });

                    if (formType === "update") {
                        const _List: any = [];
                        props.lmbc.forEach((item: any) => {
                            let _obj = { ...item };
                            if (item.id === data.id) {
                                _obj = { ..._obj, ...variables.lmbcHcp };
                                _List.push(_obj);
                            } else {
                                _List.push(_obj);
                            }
                        });
                        props.setLmbc(_List);
                    }
                }

                setLoading(false);
                setAlertToDefault();
            })
            .catch((error) => {
                setAlert({
                    message: "Something went wrong",
                    type: "danger",
                });
                setAlertToDefault();
                setLoading(false);
                toast.error(error.message);
            });
    });

    return (
        <div>
            {alert.message && alert.type && (
                <Alert alertMessage={alert.message} alertType={alert.type} />
            )}

            <FormContainer
                submitAction={onSubmit}
                submitButtonText={
                    formType === "update" ? "Update Dermatologist" : "Create Dermatologist"
                }
                noValidate
                loading={loading}
            >
                <div className="row">
                    <div className="col-lg-6">
                        <TextInput
                            id="firstName"
                            type="text"
                            labelText="First Name"
                            {...register("firstName", {
                                required: true,
                                pattern: Pattern.name,
                            })}
                            isInvalid={!!errors.firstName}
                            required
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="lastName"
                            type="text"
                            labelText="Last Name"
                            {...register("lastName", {
                                required: true,
                                pattern: Pattern.name,
                            })}
                            isInvalid={!!errors.lastName}
                            required
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="email"
                            type="email"
                            labelText="Email"
                            {...register("email", {
                                required: true,
                                pattern: Pattern.email,
                            })}
                            isInvalid={!!errors.email}
                            required
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="phone"
                            type="tel"
                            labelText="Phone"
                            {...register("phone", {
                                pattern: Pattern.number,
                            })}
                            isInvalid={!!errors.phone}
                        />
                    </div>

                    <div className="col-lg-6">
                        <SelectInput
                            id="gender"
                            labelText="Gender"
                            {...register("gender", {
                                required: true,
                            })}
                            options={GENDERS}
                            isInvalid={!!errors.gender}
                            required
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="website"
                            type="text"
                            labelText="Website"
                            {...register("website", {
                                pattern: Pattern.link,
                            })}
                            isInvalid={!!errors.website}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="imageId"
                            type="text"
                            labelText="Image ID"
                            {...register("imageId")}
                            isInvalid={!!errors.imageId}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="address"
                            type="text"
                            labelText="Address"
                            {...register("address", {
                                required: true,
                            })}
                            isInvalid={!!errors.address}
                            required
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="region"
                            type="text"
                            labelText="Region"
                            {...register("region", {
                                pattern: Pattern.name,
                            })}
                            isInvalid={!!errors.region}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="clinicName"
                            type="text"
                            labelText="Clinic Name"
                            {...register("clinicName", {
                                pattern: Pattern.name,
                                required: true,
                            })}
                            required
                            isInvalid={!!errors.clinicName}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="language"
                            type="text"
                            labelText="Language"
                            {...register("language", {
                                pattern: Pattern.name,
                            })}
                            isInvalid={!!errors.language}
                        />
                    </div>

                    <div className="col-lg-6">
                        <Controller
                            name="country"
                            render={({ field: { name, onChange, value } }) => (
                                <CountrySelectDropdown
                                    id="country"
                                    country={value ?? ""}
                                    labelText="Country"
                                    selectCountry={onChange}
                                    {...register("country", {
                                        required: true,
                                    })}
                                    required
                                    isInvalid={!!errors.country}
                                />
                            )}
                            control={control}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="monday"
                            type="text"
                            labelText="Monday"
                            {...register("monday")}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="tuesday"
                            type="text"
                            labelText="Tuesday"
                            {...register("tuesday")}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="wednesday"
                            type="text"
                            labelText="Wednesday"
                            {...register("wednesday")}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="thursday"
                            type="text"
                            labelText="Thursday"
                            {...register("thursday")}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="friday"
                            type="text"
                            labelText="Friday"
                            {...register("friday")}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="saturday"
                            type="text"
                            labelText="Saturday"
                            {...register("saturday")}
                        />
                    </div>

                    <div className="col-lg-6">
                        <TextInput
                            id="sunday"
                            type="text"
                            labelText="Sunday"
                            {...register("sunday")}
                        />
                    </div>
                </div>
            </FormContainer>
        </div>
    );
}

const mapStateToProps = (state: any) => {
    return {
        lmbc: getLmbcList(state),
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        setLmbc: (list: any) => {
            dispatch(setLmbc(list));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form);
